import { h, createContext } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import * as queries from 'utils/storefront/queries';
import {
  formatDiscountConnectors,
  formatCartLinesAjax,
} from 'utils/storefront/format-resource';
import CartService, { CART_EVENTS } from './cart-service';
import Storefront from '../utils/storefront';
import { getCookie } from '../utils/helpers';

const Context = createContext();

const CartContext = ({ children, data }) => {
  const storefront = new Storefront();
  const [cartState, setCartState] = useState(CartService.cart);
  const [loading, setLoading] = useState(CartService.loading);
  const [discountConnectors, setDiscountConnectors] = useState(null);

  const discountConnectorsType = data?.useTestDiscountConnector === true ? 'test_discount_connector' : 'discountconnector';

  const getDiscountConnectors = async () => {
    try {
      const response = await storefront.request({
        query: queries.discountConnectors(discountConnectorsType),
        variables: {
          countryCode: window.TMW.localization.countryCode,
          languageCode: window.TMW.localization.languageCode,
        },
      });

      const formattedDiscountConnectors = formatDiscountConnectors(response);
      setDiscountConnectors(formattedDiscountConnectors);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useEffect(() => {
    const listener = (event) => {
      setLoading(CartService.loading);
      setCartState(CartService.cart);
      if (event === CART_EVENTS.UPDATE) {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    };
    CartService.addListener(listener);
    setCartState(CartService.cart);
    getDiscountConnectors();
    return () => {
      CartService.removeListener(listener);
    };
  }, []);

  /**
   * Wrapper for cart API to make interactions more simple
   */
  const cart = {
    add: (...params) => CartService.add(...params),
    remove: (...params) => CartService.remove(...params),
    update: (...params) => CartService.update(...params),
    state: cartState,
  };


  const getAttributes = (line) => {
    const attributes =
      line.attributes ||
      Object.entries(line.properties || {}).map(([key, value]) => ({
        key,
        value,
      }));
    return attributes;
  };

  const getMerchandiseId = (line) =>
    line.merchandise?.product?.id || `gid://shopify/Product/${line.product_id}`;

  const getGwpAddPayload = ({
    gwpProduct,
    qualifiedProduct,
    qty,
    message,
    handle,
  }) => ({
    attributes: [
      {
        key: '_gwp',
        value: 'true',
      },
      {
        key: '_gwp_handle',
        value: handle,
      },
      {
        key: '_gwp_message',
        value: message,
      },
      {
        key: '_gwp_product',
        value: `${qualifiedProduct.product_id}`,
      },
      {
        key: '_gwp_price',
        value: gwpProduct.variants.edges[0].node.price.amount,
      },
    ],
    id: gwpProduct.variants.edges[0].node.id,
    quantity: qty,
  });

  const getQualifiedProductsQty = (products) =>
    products.reduce((total, line) => total + line.quantity, 0);

  const getGwpItem = (lines, handle) => lines.find((line) =>
      getAttributes(line).find(
        (attribute) => attribute.key === '_gwp_handle' && attribute.value === handle,
      ),
    );

  useEffect(() => {
    if (!cartState.id && !cartState.token) return;
    const lines =
      cart?.mode === 'storefront'
        ? cart?.state?.lines
        : formatCartLinesAjax(cart?.state?.items);
    const totalAmount =
      cartState.cost?.totalAmount?.amount || cartState.items_subtotal_price;
    const cartCountEl = document.querySelector('[data-cart-count]');
    const addPayload = [];
    const updatePayload = [];
    cartCountEl.textContent =
      lines.reduce((total, line) => total + line.quantity, 0) || 0;

    if (discountConnectors) {
      discountConnectors.map((connector) => {
        if (connector.discount_type === 'product') {
          const gwpRemovedByUser = getCookie(
            `disabled_gwp-${connector.handle}`,
          );

          const isMultipleAdd = connector.gwp_product?.tags?.includes('gwp-allow-multiple');

          const qualifyProductsList = connector.qualify_products_list;

          const qualifyProductIds = qualifyProductsList?.length
            && new Set(qualifyProductsList.map(product => product.id))

          const qualifyingProductsInCart = lines.filter(line =>
            qualifyProductIds.has(getMerchandiseId(line))
          );

          const hasQualifyingProducts = qualifyingProductsInCart.length > 0;

          const qualifyingProductsQty =
            hasQualifyingProducts
              ? getQualifiedProductsQty(qualifyingProductsInCart)
              : 0;

          const qtyToAdd =
            isMultipleAdd && !!qualifyingProductsQty
              ? qualifyingProductsQty
              : 1;

          const gwpInCart = getGwpItem(lines, connector.handle);

          if (hasQualifyingProducts) {
            // add gwp to cart
            // gwp is not in cart
            if (!gwpRemovedByUser && !gwpInCart) {
              addPayload.push(
                getGwpAddPayload({
                  gwpProduct: connector.gwp_product,
                  qualifiedProduct: qualifyingProductsInCart[0],
                  qty: qtyToAdd,
                  message: connector.gift_message,
                  handle: connector.handle,
                }),
              );
            }

            // limit quantity of GWP to 1
            // gwp is in cart but quantity is > 1
            if (
              gwpInCart &&
              gwpInCart?.quantity !== qtyToAdd
            ) {
              updatePayload.push({
                id: gwpInCart.key,
                quantity: qtyToAdd,
              });
            }
          }
          // remove gwp from cart
          // if qualifying products are not in cart and
          // gwp is in cart
          if (!hasQualifyingProducts && gwpInCart) {
            updatePayload.push({
              id: gwpInCart.key,
              quantity: 0,
            });
          }
        } else if (connector.discount_type === 'cartPrice') {
          const gwpRemovedByUser = getCookie(
            `disabled_gwp-${connector.handle}`,
          );

          const threshold =
            window.Shopify.currency.active === 'CAD'
              ? connector.gwp_threshold_ca
              : connector.gwp_threshold;

          const totalAmountMeetsThreshold =
            totalAmount > parseFloat(threshold * 100);

          const gwpInCart = lines.find((line) =>
            getAttributes(line).find(
              (attribute) =>
                attribute.key === '_gwp_handle' &&
                attribute.value === connector.handle,
            ),
          );

          // add gwp to cart
          // if cart total is > threshold and
          // gwp is not in cart
          if (!gwpRemovedByUser && totalAmountMeetsThreshold && !gwpInCart) {
            const gwpAddPayload = {
              attributes: [
                {
                  key: '_gwp',
                  value: 'true',
                },
                {
                  key: '_gwp_handle',
                  value: connector.handle,
                },
                {
                  key: '_gwp_message',
                  value: connector.gift_message,
                },
                {
                  key: '_gwp_threshold',
                  value: connector.gwp_threshold,
                },
                {
                  key: '_gwp_threshold_ca',
                  value: connector.gwp_threshold_ca,
                },
                {
                  key: '_gwp_price',
                  value:
                    connector.gwp_product?.variants?.edges[0]?.node?.price
                      ?.amount,
                },
              ],
              id: connector.gwp_product.variants.edges[0].node.id,
              quantity: 1,
            };
            addPayload.push(gwpAddPayload);
          }

          // no need to remove the GWP if invalid
          // that is handled by the line item script

          // update quantity of GWP
          // if cart total > threshold and
          // GWP is in cart but has quantity > 1
          if (totalAmountMeetsThreshold && gwpInCart?.quantity > 1) {
            const gwpQtyControlPayload = [
              {
                id: lines.find((line) =>
                  getAttributes(line).find(
                    (attribute) =>
                      attribute.key === '_gwp_handle' &&
                      attribute.value === connector.handle,
                  ),
                ).key,
                quantity: 1,
              },
            ];
            updatePayload.push(gwpQtyControlPayload);
          }

          if (!totalAmountMeetsThreshold && gwpInCart) {
            updatePayload.push({
              id: gwpInCart.key,
              quantity: 0,
            });
          }
        }

        return '';
      });

      // SAMPLES
      //
      // Only one sample should be in the cart at a time
      // there can be more than one sample configured, each with a different price threshold
      // the sample with the highest threshold that is still below the cart price
      //   is the sample that should be added to the cart.
      // all other samples should be removed from the cart.

      const sampleDiscounts = discountConnectors
        .filter((discount) => discount.discount_type === 'sample')
        .sort(
          (a, b) => parseFloat(b.gwp_threshold) - parseFloat(a.gwp_threshold),
        );

      // mostFitSample is the sample with the highest price below the cart price
      // and that has not been removed by the user (cookie)

      const mostFitSample = sampleDiscounts.find((connector) => {
        const threshold =
          window.Shopify.currency.active === 'CAD'
            ? connector.gwp_threshold_ca
            : connector.gwp_threshold;

        return (
          totalAmount > parseFloat(threshold * 100) &&
          !getCookie(`disabled_gwp-${connector.handle}`)
        );
      });

      const mostFitSampleInCart =
        mostFitSample &&
        lines.find((line) =>
          getAttributes(line).find(
            (attribute) =>
              attribute.key === '_isSample' &&
              attribute.value === mostFitSample.handle,
          ),
        );

      // any samples in the cart that dont match the mostFitSample
      const unfitSamplesInCart = mostFitSample
        ? lines.filter((line) =>
            getAttributes(line).find(
              (attribute) =>
                attribute.key === '_isSample' &&
                attribute.value !== mostFitSample.handle,
            ),
          )
        : lines.filter((line) =>
            getAttributes(line).find(
              (attribute) => attribute.key === '_isSample',
            ),
          );

      // remove all unfit samples
      // the line item script only removes an item if it doesnt qualify based on price
      // it doesnt make sure only the correct sample is in the cart
      // so we need to do that here.
      if (unfitSamplesInCart && unfitSamplesInCart.length > 0) {
        // eslint-disable-next-line array-callback-return
        unfitSamplesInCart.map((sample) => {
          updatePayload.push({
            id: sample.key,
            quantity: 0,
          });
        });
      }

      // add most fit sample to cart
      // if valid sample meets threshold and
      // sample is not in cart
      if (mostFitSample && !mostFitSampleInCart) {
        const mostFitSampleAddPayload = {
          attributes: [
            {
              key: '_gwp',
              value: 'true',
            },
            {
              key: '_gwp_handle',
              value: mostFitSample.handle,
            },
            {
              key: '_gwp_message',
              value: mostFitSample.gift_message,
            },
            {
              key: '_gwp_threshold',
              value: mostFitSample.gwp_threshold,
            },
            {
              key: '_gwp_threshold_ca',
              value: mostFitSample.gwp_threshold_ca,
            },
            {
              key: '_isSample',
              value: mostFitSample.handle,
            },
          ],
          id: mostFitSample.gwp_product.variants.edges[0].node.id,
          quantity: 1,
        };
        addPayload.push(mostFitSampleAddPayload);
      }

      // restrict the quantity to 1
      // if valid sample meets threshold and
      // that sample is in the cart but with quantity > 1
      if (mostFitSample && mostFitSampleInCart?.quantity > 1) {
        const sampleQtyControlPayload = [
          {
            id: lines.find((line) =>
              getAttributes(line).find(
                (attribute) =>
                  attribute.key === '_isSample' &&
                  attribute.value === mostFitSample?.handle,
              ),
            ).key,
            quantity: 1,
          },
        ];
        updatePayload.push(sampleQtyControlPayload);
      }

      if (!mostFitSample && mostFitSampleInCart) {
        updatePayload.push({
          id: mostFitSampleInCart.key,
          quantity: 0,
        });
      }
    }

    if (cartState.item_count > 0 && cartState.total_price === 0) {
      // eslint-disable-next-line array-callback-return, consistent-return
      cartState.items.map((item) => {
        if (!updatePayload.find((updateItem) => updateItem.key === item.key)) {
          updatePayload.push({
            id: item.key,
            quantity: 0,
          });
        }
      });
    }

    if (updatePayload.length > 0) {
      cart.update(updatePayload, true);
    }

    if (addPayload.length > 0) {
      cart.add(addPayload);
    }
  }, [cartState.item_count, discountConnectors]);

  return (
    <Context.Provider value={{ cart, cartLoading: loading, data }}>
      {children}
    </Context.Provider>
  );
};

export const withCartContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => <Rendered {...props} {...value} />}
    </Context.Consumer>
  );

export default CartContext;
